import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Loader2, RefreshCw, Send, ShieldCheck, LinkIcon, ExternalLink, MessageSquare } from "lucide-react";
import { analyzeVideo, adminHeaders } from "../lib/api";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacebookPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState({});
  const [analysis, setAnalysis] = useState({});

  const refreshStatus = async () => {
    const { data } = await axios.get(`${API}/facebook/status`);
    setStatus(data);
  };

  useEffect(() => {
    refreshStatus();
    const s = params.get("status");
    if (s === "connected") toast.success(T("Facebook connecté."));
    if (s === "error") toast.error(`Connexion impossible : ${params.get("reason") || T("erreur")}`);
  }, [params]);

  const loadPages = async () => {
    setLoading((l) => ({ ...l, pages: true }));
    try {
      const { data } = await axios.get(`${API}/facebook/pages`);
      setPages(data.pages || []);
      if (data.pages?.[0]) selectPage(data.pages[0]);
    } catch (e) { toast.error(T("Impossible de charger les pages.")); }
    finally { setLoading((l) => ({ ...l, pages: false })); }
  };

  const selectPage = async (p) => {
    setActivePage(p); setPosts([]);
    setLoading((l) => ({ ...l, posts: true }));
    try {
      const { data } = await axios.get(`${API}/facebook/pages/${p.id}/posts`);
      setPosts(data.posts || []);
    } catch { toast.error(T("Impossible de charger les publications.")); }
    finally { setLoading((l) => ({ ...l, posts: false })); }
  };

  const analyse = async (post) => {
    setLoading((l) => ({ ...l, [`a-${post.id}`]: true }));
    try {
      const data = await analyzeVideo({
        url: post.permalink_url || "(post Facebook)",
        country: "Sénégal",
        transcript: post.message || "",
        notes: `Auteur: ${T(activePage?.name)}. Type: ${post.attachments?.data?.[0]?.media_type || "post"}.`,
      });
      setAnalysis((a) => ({ ...a, [post.id]: data.text }));
    } catch { toast.error(T("Analyse impossible.")); }
    finally { setLoading((l) => ({ ...l, [`a-${post.id}`]: false })); }
  };

  const postComment = async (post, draft) => {
    if (!draft?.trim()) { toast.error("Commentaire vide."); return; }
    try {
      await axios.post(`${API}/facebook/comment`, { page_id: activePage.id, post_id: post.id, message: draft }, { headers: adminHeaders() });
      toast.success(T("Commentaire publié sur Facebook."));
    } catch (e) { toast.error(e?.response?.status === 401 ? T("Publication réservée à l'administrateur.") : T("Échec de la publication (permissions insuffisantes ou App Review en cours).")); }
  };

  const connect = async () => {
    try {
      const { data } = await axios.post(`${API}/facebook/login`, {}, { headers: adminHeaders() });
      window.location.href = data.url;
    } catch { toast.error(T("Connexion réservée à l'administrateur.")); }
  };

  const disconnect = async () => {
    try {
      await axios.post(`${API}/facebook/disconnect`, {}, { headers: adminHeaders() });
      setStatus({ connected: false }); setPages([]); setPosts([]); setActivePage(null);
      toast.success(T("Facebook déconnecté."));
    } catch { toast.error(T("Action réservée à l'administrateur.")); }
  };

  if (status === null) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 text-[color:var(--papicoo-muted)]">{T("Chargement…")}</div>;
  }

  return (
    <div data-testid="page-facebook" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Facebook — intégration officielle Meta")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Tes Pages.")}<br />
        <span className="italic font-light">{T("Analysées. Commentées. Avec ton accord.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-3xl leading-relaxed">
        {T("Connexion")} <strong>{T("OAuth officielle Meta")}</strong> {T("(jamais de mot de passe). Permissions B activées : lecture des pages, posts, commentaires + publication commentaire")} <strong>{T("uniquement sur ton clic")}</strong>.
      </p>

      {!status.connected ? (
        <div className="mt-8 papicoo-card p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <Facebook size={32} className="text-[#1877F2]" />
            <div className="flex-1">
              <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Connecter Facebook")}</div>
              <p className="text-sm text-[color:var(--papicoo-muted)] mt-1">{T("Tu seras redirigé vers Facebook pour valider les permissions. Aucun mot de passe ne transite par Papicoo.")}</p>
              <button type="button" data-testid="fb-connect-btn" onClick={connect} className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary">
                <LinkIcon size={16} /> {T("Se connecter avec Facebook")}
              </button>
              <div className="mt-4 text-xs text-[color:var(--papicoo-muted)] inline-flex items-center gap-2">
                <ShieldCheck size={14} /> {T("Tu peux révoquer l'accès à tout moment depuis Facebook → Paramètres → Apps et sites web.")}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          <div className="papicoo-card p-5 flex flex-wrap items-center gap-3 justify-between">
            <div className="text-sm">
              {T("Connecté en tant que")} <strong>{status.fb_name}</strong> {T("· ID")} <code className="text-xs">{status.fb_user_id}</code>
            </div>
            <div className="flex gap-2">
              <button data-testid="fb-load-pages" onClick={loadPages} className="inline-flex items-center gap-2 px-4 py-2 rounded-full papicoo-btn-gold text-sm" disabled={loading.pages}>
                {loading.pages ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Charger mes pages
              </button>
              <button data-testid="fb-disconnect" onClick={disconnect} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[color:var(--papicoo-border)] text-sm">{T("Déconnecter")}</button>
            </div>
          </div>

          {pages.length > 0 && (
            <div className="papicoo-card p-5">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)] mb-3">{T("Mes Pages")}</div>
              <div className="flex flex-wrap gap-2">
                {pages.map((p) => (
                  <button key={p.id} data-testid={`fb-page-${p.id}`} onClick={() => selectPage(p)} className={`px-4 py-2 text-sm rounded-full ${activePage?.id === p.id ? "papicoo-btn-primary" : "bg-white border border-[color:var(--papicoo-border)]"}`}>
                    {T(p.name)} {p.fan_count ? <span className="opacity-60 ml-1">· {p.fan_count}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activePage && (
            <div className="space-y-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-accent)]">Publications · {T(activePage.name)}</div>
              {loading.posts && <div className="text-sm text-[color:var(--papicoo-muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> {T("Chargement…")}</div>}
              {posts.map((post) => (
                <div key={post.id} className="papicoo-card p-5" data-testid={`fb-post-${post.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-xs text-[color:var(--papicoo-muted)]">{new Date(post.created_time).toLocaleString("fr-FR")}</div>
                    {post.permalink_url && (
                      <a href={post.permalink_url} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-ink)] papicoo-link-underline">
                        {T("Voir sur FB")} <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  {post.message && <p className="text-sm mt-2 whitespace-pre-wrap leading-relaxed">{post.message}</p>}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button data-testid={`fb-analyze-${post.id}`} onClick={() => analyse(post)} disabled={loading[`a-${post.id}`]} className="inline-flex items-center gap-2 px-4 py-2 rounded-full papicoo-btn-primary text-sm disabled:opacity-50">
                      {loading[`a-${post.id}`] ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} Analyse Papicoo
                    </button>
                  </div>

                  {analysis[post.id] && (
                    <div className="mt-4 border-t border-[color:var(--papicoo-border)] pt-4">
                      <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed">{analysis[post.id]}</pre>
                      <div className="mt-3">
                        <CommentComposer onSend={(draft) => postComment(post, draft)} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {!loading.posts && posts.length === 0 && (
                <div className="papicoo-card p-5 text-sm italic text-[color:var(--papicoo-muted)]">{T("Aucune publication récente sur cette page.")}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommentComposer({ onSend }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2">
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder={T("Écris ou colle un commentaire à publier sur ta page…")}
        className="flex-1 min-w-0 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
      />
      <button onClick={() => { onSend(v); setV(""); }} className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full papicoo-btn-gold text-sm">
        <Send size={14} /> {T("Publier")}
      </button>
    </div>
  );
}
